/**
 * Email signature POOL endpoints (Jul 2026) — multi-signature "pool" model.
 *
 * BE source of truth: ``ando-agent/src/api/v1/endpoints/email_signatures.py``.
 * Replaces the single per-user ``signature_html`` override (still exposed via
 * the legacy ``/email/signature`` helpers in ``./emailTemplates.ts`` for
 * backward compat) with a NAMED pool of signatures + a per-mailbox default
 * binding:
 *
 *   - the pool: ``GET/POST/PATCH/DELETE /email/signatures`` — user- and
 *     org-scoped rows; org rows are read-only for the operator (use only).
 *   - the user's global default: ``POST /email/signatures/{id}/default``.
 *   - per-mailbox binding: ``GET /email/signature/accounts`` +
 *     ``PUT /email/signature/accounts/{connection_id}`` — pick which pool
 *     signature is a mailbox's default (or ``null`` = follow the global one).
 *
 * The resolved signature for a given FROM mailbox is still fetched via
 * ``getEffectiveSignature(account)`` in ``./emailTemplates.ts`` (its shape
 * gained a ``signature_id`` — see ``EffectiveSignature`` there).
 */

import { apiClient } from "../ApiClient.js";

// ─── Types ───────────────────────────────────────────────────────────────

/**
 * A single named signature in the pool.
 *   - ``scope: "user"`` — authored by the operator; fully editable.
 *   - ``scope: "org"``  — set by a teammate for the whole org; READ-ONLY here
 *     (the operator can pick it as a mailbox default but not edit/delete it).
 */
export interface Signature {
  id: string;
  name: string;
  signature_html: string;
  /** The user's global default signature (at most one ``true``). */
  is_default: boolean;
  scope: "user" | "org";
  updated_at: string;
}

export interface CreateSignatureInput {
  name: string;
  signature_html: string;
}

/** Partial update — any subset of the editable fields. */
export interface UpdateSignatureInput {
  name?: string;
  signature_html?: string;
}

/**
 * A connected mailbox and which pool signature is ITS default. ``null`` =
 * follow the user's global default.
 */
export interface AccountSignatureBinding {
  connection_id: string;
  email: string;
  provider: string;
  default_signature_id: string | null;
}

// ─── Pool ────────────────────────────────────────────────────────────────

/** GET ``/email/signatures`` — the pool (user + org-scoped rows). */
export async function listSignatures(): Promise<Signature[]> {
  const res = await apiClient.get<{ signatures: Signature[] }>(
    "/email/signatures",
  );
  return res.signatures;
}

/** POST ``/email/signatures`` — create a personal (user-scoped) signature. */
export async function createSignature(
  input: CreateSignatureInput,
): Promise<Signature> {
  return await apiClient.post<Signature>("/email/signatures", input);
}

/** PATCH ``/email/signatures/{id}`` — partial update of a user-scoped row. */
export async function updateSignature(
  id: string,
  input: UpdateSignatureInput,
): Promise<Signature> {
  return await apiClient.patch<Signature>(
    `/email/signatures/${encodeURIComponent(id)}`,
    input,
  );
}

/** DELETE ``/email/signatures/{id}`` → 204. */
export async function deleteSignature(id: string): Promise<void> {
  await apiClient.delete<void>(`/email/signatures/${encodeURIComponent(id)}`);
}

/** POST ``/email/signatures/{id}/default`` — set the user's global default. */
export async function setDefaultSignature(id: string): Promise<Signature> {
  return await apiClient.post<Signature>(
    `/email/signatures/${encodeURIComponent(id)}/default`,
    {},
  );
}

// ─── Per-mailbox binding ─────────────────────────────────────────────────

/** GET ``/email/signature/accounts`` — connected mailboxes + their binding. */
export async function listSignatureAccounts(): Promise<AccountSignatureBinding[]> {
  const res = await apiClient.get<{ accounts: AccountSignatureBinding[] }>(
    "/email/signature/accounts",
  );
  return res.accounts;
}

/**
 * PUT ``/email/signature/accounts/{connection_id}`` — bind a pool signature to
 * a mailbox as its default. Pass ``null`` to clear the binding (fall back to
 * the user's global default).
 */
export async function setAccountSignature(
  connectionId: string,
  signatureId: string | null,
): Promise<void> {
  await apiClient.put<void>(
    `/email/signature/accounts/${encodeURIComponent(connectionId)}`,
    { signature_id: signatureId },
  );
}
